import { DG } from '@cotera/sdk/core';

type EditorStatePayload = { type: 'doc'; content?: unknown[] };

/** Params shape shared by chat submission flows (submit, pending, new chat). */
export type ChatSubmissionParams = {
  text?: string;
  editorState?: EditorStatePayload;
  files?: unknown[];
  initialArtifacts?: unknown[];
  initialToolKeys?: unknown[];
  initialActions?: unknown[];
  /** Explicit saved-agent id (chat context.agentId). */
  agentId?: string;
};

/** True when user provided text, editor content, or files. */
export function hasUserContent(params: ChatSubmissionParams): boolean {
  return (
    (params.text?.trim().length ?? 0) > 0 ||
    (params.editorState !== undefined &&
      (params.editorState.content?.length ?? 0) > 0) ||
    (params.files?.length ?? 0) > 0
  );
}

/** True when submission has initial context (artifacts, tools, actions, or linked agent). */
export function hasInitialContext(params: ChatSubmissionParams): boolean {
  return (
    (params.initialArtifacts?.length ?? 0) > 0 ||
    (params.initialToolKeys?.length ?? 0) > 0 ||
    (params.initialActions?.length ?? 0) > 0 ||
    (typeof params.agentId === 'string' && params.agentId.length > 0)
  );
}

/** True when there is content to submit (user content or initial context). */
export function hasContentToSubmit(params: ChatSubmissionParams): boolean {
  return hasUserContent(params) || hasInitialContext(params);
}

/** True when submission has only initial context and no user-provided content. Used to mark the first message as hidden. */
export function hasOnlyInitialContext(params: ChatSubmissionParams): boolean {
  return hasInitialContext(params) && !hasUserContent(params);
}

/**
 * Returns the plain text of a message (markdown for rich text), or '' if empty.
 * Handles both plain text and editor-state (rich text) payloads.
 */
export function getMessageTextFromPayload(payload: {
  text?: string;
  editorState?: EditorStatePayload;
}): string {
  const text = (payload.text ?? '').trim();
  if (text.length > 0) {
    return text;
  }
  if (
    payload.editorState !== undefined &&
    (payload.editorState.content?.length ?? 0) > 0
  ) {
    try {
      const editor = new DG.DocumentEditor.EditorState(
        payload.editorState as DG.DocumentEditor.DocumentState
      );
      return editor.toPromptMarkdown(DG.BuildExplorer.empty(), 'simple');
    } catch {
      return '';
    }
  }
  return '';
}

/**
 * Returns the character count of a message for analytics.
 * Handles both plain text and editor-state (rich text) payloads.
 */
export function getMessageLengthFromPayload(payload: {
  text?: string;
  editorState?: EditorStatePayload;
}): number {
  const textLength = (payload.text ?? '').trim().length;
  if (textLength > 0) {
    return textLength;
  }
  if (
    payload.editorState !== undefined &&
    (payload.editorState.content?.length ?? 0) > 0
  ) {
    try {
      const editor = new DG.DocumentEditor.EditorState(
        payload.editorState as DG.DocumentEditor.DocumentState
      );
      return editor.toPromptMarkdown(DG.BuildExplorer.empty(), 'simple').length;
    } catch {
      return 0;
    }
  }
  return 0;
}
